import React from 'react';
import { Loan, SortState, SortField } from '../types/loan';
import { ChevronUpIcon, ChevronDownIcon, UserPlusIcon, SearchIcon } from 'lucide-react';
interface LoanTableProps {
  loans: Loan[];
  currentMarketRate: number;
  selectedLoanIds: Set<string>;
  onToggleSelection: (loanId: string) => void;
  onToggleAll: (selectAll: boolean) => void;
  sortState: SortState;
  onSort: (field: SortField) => void;
  onCreateLead: (loan: Loan) => void;
}
export function LoanTable({
  loans,
  currentMarketRate,
  selectedLoanIds,
  onToggleSelection,
  onToggleAll,
  sortState,
  onSort,
  onCreateLead
}: LoanTableProps) {
  const allSelected = loans.length > 0 && selectedLoanIds.size === loans.length;
  const someSelected = selectedLoanIds.size > 0 && selectedLoanIds.size < loans.length;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getRateColorClass = (diff: number) => {
    if (diff > 2.0) return 'text-red-700 bg-red-50 ring-red-600/20';
    if (diff >= 1.0) return 'text-orange-700 bg-orange-50 ring-orange-600/20';
    return 'text-green-700 bg-green-50 ring-green-600/20';
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortState.field !== field) return <ChevronDownIcon className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-58" />;
    return sortState.direction === 'asc' ? <ChevronUpIcon className="w-4 h-4 text-violet-600" data-id="element-59" /> : <ChevronDownIcon className="w-4 h-4 text-violet-600" data-id="element-60" />;
  };
  const thClass = 'px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors select-none';
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-61">
      <div className="overflow-x-auto" data-id="element-62">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-63">
          <thead className="bg-gray-50/80 border-b border-gray-200" data-id="element-64">
            <tr data-id="element-65">
              <th scope="col" className="px-4 py-3.5 text-left" data-id="element-66">
                <input type="checkbox" className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer" checked={allSelected} ref={input => {
                if (input) input.indeterminate = someSelected;
              }} onChange={e => onToggleAll(e.target.checked)} data-id="element-67" />
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('borrowerName')} data-id="element-68">
                <div className="flex items-center gap-1" data-id="element-69">
                  Borrower <SortIcon field="borrowerName" data-id="element-70" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('loanAmount')} data-id="element-71">
                <div className="flex items-center gap-1" data-id="element-72">
                  Loan Amount <SortIcon field="loanAmount" data-id="element-73" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('interestRate')} data-id="element-74">
                <div className="flex items-center gap-1" data-id="element-75">
                  Rate <SortIcon field="interestRate" data-id="element-76" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('rateDifference')} data-id="element-77">
                <div className="flex items-center gap-1" data-id="element-78">
                  Above Market <SortIcon field="rateDifference" data-id="element-79" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('closeDate')} data-id="element-80">
                <div className="flex items-center gap-1" data-id="element-81">
                  Close Date <SortIcon field="closeDate" data-id="element-82" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('loanTerm')} data-id="element-83">
                <div className="flex items-center gap-1" data-id="element-84">
                  Term <SortIcon field="loanTerm" data-id="element-85" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('propertyType')} data-id="element-86">
                <div className="flex items-center gap-1" data-id="element-87">
                  Property <SortIcon field="propertyType" data-id="element-88" />
                </div>
              </th>
              <th scope="col" className="px-4 py-3.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-89">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-90">
            {loans.length === 0 ? <tr data-id="element-91">
                <td colSpan={9} className="px-6 py-16 text-center" data-id="element-92">
                  <div className="flex flex-col items-center justify-center" data-id="element-93">
                    <div className="text-gray-300 mb-3" data-id="element-94">
                      <SearchIcon className="w-10 h-10" data-id="element-95" />
                    </div>
                    <p className="text-gray-500 font-medium" data-id="element-96">
                      No loans found matching your criteria.
                    </p>
                    <p className="text-sm text-gray-400 mt-1" data-id="element-97">
                      Adjust your market rate, date range, or threshold above.
                    </p>
                  </div>
                </td>
              </tr> : loans.map(loan => {
            const rateDiff = loan.interestRate - currentMarketRate;
            const isSelected = selectedLoanIds.has(loan.id);
            return <tr key={loan.id} className={`hover:bg-gray-50 transition-colors even:bg-gray-50/30 ${isSelected ? 'bg-violet-50/50' : ''}`} data-id="element-98">
                    <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-99">
                      <input type="checkbox" className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer" checked={isSelected} onChange={() => onToggleSelection(loan.id)} data-id="element-100" />
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-101">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-102">
                        {loan.borrowerName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-103">
                        {loan.id}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-104">
                      {formatCurrency(loan.loanAmount)}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-105">
                      {loan.interestRate.toFixed(3)}%
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-106">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset tabular-nums ${getRateColorClass(rateDiff)}`} data-id="element-107">
                        +{rateDiff.toFixed(3)}%
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-108">
                      {formatDate(loan.closeDate)}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-109">
                      {loan.loanTerm} mo
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700" data-id="element-110">
                      {loan.propertyType}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-medium" data-id="element-111">
                      <button onClick={() => onCreateLead(loan)} className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-900 bg-violet-50 hover:bg-violet-100 px-3 py-1.5 rounded-lg text-xs transition-colors" data-id="element-112">
                        <UserPlusIcon className="w-4 h-4" data-id="element-113" />
                        <span data-id="element-114">Create Lead</span>
                      </button>
                    </td>
                  </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}